import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as Sentry from "@sentry/react";
import { Box, Checkbox, Notification, Tabs, Text, toast } from "@telia/teddy";
import { useSelector, useDispatch } from "react-redux";
import {
    isBroadbandSubscription,
    getInitialOfferingCode,
    getInitialSubscriptionGroup,
    getTabStateForOfferingCode,
    getInitialOfferingCodeForCategory,
    getFirstSubscriptionForMobileSubTab,
} from "./utils";
import {
    isZeroPlaySubscription,
    isPrepaidSubscription,
    isNonMhsSubscription,
    isHandsetWithLeaseCode,
} from "../../../../../../shared/subscriptionCodeUtils";
import { CategoryTab, TvSubTab, TjenesteSubTab, MobileSubTab, BroadbandSubTab } from "./types";
import { CART_ITEM_SUBSCRIPTION_TYPES, CUSTOMER_TYPE, NEW_ACCOUNT_VALUE } from "../../../../../../constants";
import { MAX_AGE_BARN, MAX_NUMBER_OF_ALLOWED_SUBSCRIPTIONS } from "../../../../../../constants/app";
import PAGES from "../../../../../../constants/pages";
import {
    BLOCKED_SIM_ONLY_OFFERING_CODES,
    OfferingCode,
    RDSubscription,
    RootState,
    SERVICE_SUBSCRIPTION_CODES,
} from "../../../../../../models";
import { getAllItemsOfType } from "../../../../checkout/state/selectors/cartSelectors";
import { setActiveSubscription, removeItemByIdFromCart } from "../../../../checkout/state/actions/cartActions";
import * as AT from "../../../../checkout/state/actionTypes";
import { getSubscriptionCode, getSubscriptions } from "../../../../checkout/state/selectors/subscriptionsSelectors";
import { isTypeLoading } from "../../../../loading/state/selectors";
import { checkCustomerPhoneNumber, prefillCustomerForm } from "../../../state/actions/customerActions";
import { setTraadlosTabActive } from "../../../state/actions/fwaActions";
import { saveFwaPackageToOrder } from "../../../fwaApi";
import {
    getFwaSelectedPackage,
    getFwaSelectedPackageId,
    getIsTraadlosTabActive,
} from "../../../state/selectors/fwaSelectors";
import { fetchAvailableNumbers, setSelectedPhoneNumber } from "../../../state/actions/phoneNumberActions";
import {
    addExistingSubscriptionSkeletonToCart,
    addSubscriptionChangeToCart,
    clearExistingSubscriptionForChange,
    nextSubscriptionStep,
    setExistingSubscriptionForChange,
    setIsAddingSubscription,
    setPrepaidWithStartpakke,
    setSubscriptionStep,
} from "../../../state/actions/subscriptionStepsActions";
import {
    getCustomerExistingAccounts,
    getCustomerNewSubscriptionsIcim,
    getCustomerSelectedAccountId,
    getExistingSubscriptionForChange,
    getIsPrepaid,
    getCustomerCreditCheck,
} from "../../../state/selectors/customerSelectors";
import { isAddingSubscription as isAddingSubscriptionSelector } from "../../../state/selectors/subscriptionStepsSelectors";
import { isCountableSubscription, isSubscriptionActive } from "../../../utils";
import {
    getAvailablePricePlans,
    getAvailableIcims,
    getBreakageFeeAmount,
    getSelectedPanel,
} from "../../../state/selectors/phoneNumbersSelectors";
import { SubscriptionStepsControls } from "../../molecules/SubscriptionStepsControls";
import TradlosAddressSearch from "./TradlosAddressSearch";
import SubscriptionInfo from "./SubscriptionInfo";
import "./ChooseSubscription.less";
import { handleCreditCheck, resetCustomerInfo, setValue } from "../../../state/actions";
import { PhoneCheckResult } from "../../../../../../models/phoneNumberCheck";
import { RETAILER_CHOOSE_PHONE_NUMBER_SWITCH_PANEL, RETAILER_PERFORM_CREDIT_CHECK } from "../../../state/actionTypes";
import { SubscriptionGroup } from "../../../../../../models/state";
import GroupSelect from "./GroupSelect";
import SubscriptionSelect from "./SubscriptionSelect";

interface Props {
    onSelectedOfferingCodeChange?: (offeringCode?: string) => void;
    showOnlyMobileTab?: boolean;
    subscriptionId?: string;
}

const DEFAULT_SUBSCRIPTION_GROUP: SubscriptionGroup = "simdeal";

const TELIA_TRYGG_INDIVIDUAL_OFFERING: (typeof SERVICE_SUBSCRIPTION_CODES)[number] = "TRYGG_B2C_INDIVIDUAL";

const ChooseSubscription: React.FC<Props> = ({
    subscriptionId,
    onSelectedOfferingCodeChange,
    showOnlyMobileTab: showOnlyMobileTabProp,
}) => {
    const dispatch = useDispatch();
    const isAddingSubscription = useSelector((state: RootState) =>
        isTypeLoading(state, AT.CHECKOUT_ADD_SUBSCRIPTION_TO_CART),
    );
    const isCheckingPhoneNumber = useSelector((state: RootState) =>
        isTypeLoading(state, AT.CHECKOUT_CHECK_NEW_MEMBER_PHONE_NUMBER),
    );
    const isPerformingCreditCheck = useSelector((state: RootState) =>
        isTypeLoading(state, RETAILER_PERFORM_CREDIT_CHECK),
    );
    const isProcessing = isAddingSubscription || isCheckingPhoneNumber || isPerformingCreditCheck;
    const isTraadlosTabActive = useSelector(getIsTraadlosTabActive);
    const fwaSelectedPackageId = useSelector(getFwaSelectedPackageId);
    const fwaSelectedPackage = useSelector(getFwaSelectedPackage);
    const currentOfferingCode = useSelector((state: RootState) => getSubscriptionCode(state, subscriptionId));
    const existingSubscriptionForChange = useSelector(getExistingSubscriptionForChange);
    const pricePlansKey = subscriptionId || existingSubscriptionForChange?.phoneNumber;
    const icims = useSelector((state: RootState) => getCustomerNewSubscriptionsIcim(state));
    const availablePricePlans = useSelector((state: RootState) => getAvailablePricePlans(state, pricePlansKey));
    const availableIcims = useSelector((state: RootState) => getAvailableIcims(state, pricePlansKey));
    const breakageFeeAmount = useSelector((state: RootState) => getBreakageFeeAmount(state, subscriptionId));
    const creditCheck = useSelector((state: RootState) => getCustomerCreditCheck(state));
    const checkoutSubscriptionsState = useSelector(getSubscriptions);
    const checkoutSubscriptions = useMemo(
        () => Object.values(checkoutSubscriptionsState),
        [checkoutSubscriptionsState],
    );

    // Local state for isPrepaidWithStartpakke when creating new subscriptions
    const [localIsPrepaidWithStartpakke, setLocalIsPrepaidWithStartpakke] = useState(false);
    const [validationError, setValidationError] = useState("");

    // For existing customers: show all subscriptions and ICIMs (like for new customers)
    // Validation happens when agent clicks "Continue" button
    const filteredIcims = useMemo(() => icims, [icims]);

    // All subscriptions come from filteredIcims — flatten all groups per category
    const allMhsSubscriptions = useMemo(() => filteredIcims.mhs.flatMap((g) => g.subscriptions), [filteredIcims.mhs]);
    const allMbbSubscriptions = useMemo(() => filteredIcims.mbb.flatMap((g) => g.subscriptions), [filteredIcims.mbb]);
    const allPrepaidSubscriptions = useMemo(
        () => filteredIcims.prepaid.flatMap((g) => g.subscriptions),
        [filteredIcims.prepaid],
    );
    const allVasSubscriptions = useMemo(() => filteredIcims.vas.flatMap((g) => g.subscriptions), [filteredIcims.vas]);

    const allSubscriptions = useMemo(
        () => [...allMhsSubscriptions, ...allMbbSubscriptions, ...allPrepaidSubscriptions, ...allVasSubscriptions],
        [allMhsSubscriptions, allMbbSubscriptions, allPrepaidSubscriptions, allVasSubscriptions],
    );

    const initialOfferingCode =
        currentOfferingCode || (!subscriptionId ? existingSubscriptionForChange?.offeringCode : undefined);
    const initialTabState = getTabStateForOfferingCode(initialOfferingCode, allSubscriptions);
    const [activeCategory, setActiveCategory] = useState<CategoryTab>(initialTabState.category);
    const [activeMobileTab, setActiveMobileTab] = useState<MobileSubTab>(initialTabState.mobileSubTab);
    const [activeBroadbandTab, setActiveBroadbandTab] = useState<BroadbandSubTab>(initialTabState.broadbandSubTab);
    const [activeTjenesteTab, setActiveTjenesteTab] = useState<TjenesteSubTab>("TRYGGHET");
    const [activeTvTab, setActiveTvTab] = useState<TvSubTab>("TV_BOX");
    const [selectedGroup, setSelectedGroup] = useState<SubscriptionGroup>(() => {
        if (!initialOfferingCode) {
            return getInitialSubscriptionGroup(initialTabState, filteredIcims, DEFAULT_SUBSCRIPTION_GROUP);
        }
        const [pricePlan, icimValue] = initialOfferingCode.split(".");
        const allGroups = [...icims.mhs, ...icims.mbb, ...icims.prepaid, ...icims.vas];
        const matchingGroup = allGroups.find((g) =>
            g.subscriptions.some((s) => s.offeringCode === pricePlan && s.icim === icimValue),
        );
        return matchingGroup?.group ?? "regular";
    });
    const [selectedOfferingCode, setSelectedOfferingCode] = useState<string | undefined>(() =>
        getInitialOfferingCode(initialOfferingCode || undefined, initialTabState, filteredIcims, selectedGroup),
    );
    const isSelectedOfferingWithLease = isHandsetWithLeaseCode(selectedOfferingCode as OfferingCode | undefined);

    const isSubscriptionChangeScopeChange = existingSubscriptionForChange?.changeScope === "subscriptionChange";
    const selectedPanel = useSelector((state: RootState) => getSelectedPanel(state));
    const isPrepaid = useSelector((state: RootState) => getIsPrepaid(state));
    const showOnlyPrepaidTab = isPrepaid && !existingSubscriptionForChange;
    const showOnlyMobileTab = showOnlyMobileTabProp ?? (!!existingSubscriptionForChange || showOnlyPrepaidTab);

    const existingAccounts = useSelector((state: RootState) => getCustomerExistingAccounts(state));
    const selectedAccountId = useSelector((state: RootState) => getCustomerSelectedAccountId(state));
    const isInAddSubscriptionFlow = useSelector(isAddingSubscriptionSelector);
    const hasNewOrPortInSubscription = checkoutSubscriptions.some(
        (subscription) =>
            (subscription.phoneNumber?.check?.type === CUSTOMER_TYPE.NEW ||
                subscription.phoneNumber?.check?.type === CUSTOMER_TYPE.PORT_IN ||
                subscription.phoneNumber?.check?.type === CUSTOMER_TYPE.NEW_OR_PORT_IN) &&
            !isZeroPlaySubscription(subscription.code),
    );
    const showExistingCustomerNewSubscriptionCreditCheckInfo =
        existingAccounts.length > 0 &&
        !creditCheck?.performed &&
        !isPrepaid &&
        ((isInAddSubscriptionFlow && !existingSubscriptionForChange) || hasNewOrPortInSubscription);
    const showCreditCheckInfo =
        existingAccounts.length > 0 && !creditCheck?.performed && !isPrepaid && isSelectedOfferingWithLease;

    const selectedPricePlan = selectedOfferingCode?.split(".")[0] ?? undefined;

    // Mobile offering codes for the showOnlyMobileTab effect
    const mobileOfferingCodes = useMemo(
        () => allMhsSubscriptions.map((sub) => `${sub.offeringCode}.${sub.icim}`).filter(Boolean),
        [allMhsSubscriptions],
    );

    const cartSubscriptionItems = useSelector((state: RootState) =>
        getAllItemsOfType(state, ...CART_ITEM_SUBSCRIPTION_TYPES),
    );
    const offeringCodesInCartAndAccount = useMemo(() => {
        const selectedAccount = existingAccounts.find((acc) => acc.accountId === selectedAccountId);
        const cartPhoneNumbers = new Set(
            Object.entries(checkoutSubscriptionsState)
                .map(([, sub]) => sub.phoneNumber?.selectedPhoneNumber)
                .filter((p): p is string => !!p),
        );
        const selectedAccountCodes = (selectedAccount?.subscriptions ?? [])
            .filter(isSubscriptionActive)
            .filter((s) => !cartPhoneNumbers.has(s.phoneNumber))
            .filter((s) => s.phoneNumber !== existingSubscriptionForChange?.phoneNumber)
            .map((s) => s.offeringCode)
            .filter((code): code is OfferingCode => !!code);
        const cartCodes = cartSubscriptionItems
            .filter((item) => item.bundleId !== subscriptionId)
            .filter((item) => {
                if (!item.bundleId) return true;
                const sub = checkoutSubscriptionsState[item.bundleId];
                const targetAccountId = sub?.phoneNumber?.accountId;
                return targetAccountId ? targetAccountId === selectedAccountId : true;
            })
            .map((item) => item.id?.split(".")[0])
            .filter(Boolean);
        return [...cartCodes, ...selectedAccountCodes];
    }, [
        cartSubscriptionItems,
        checkoutSubscriptions,
        existingAccounts,
        existingSubscriptionForChange?.phoneNumber,
        selectedAccountId,
        subscriptionId,
    ]);
    const hasTeliaTryggIndividualInCart = useMemo(
        () =>
            cartSubscriptionItems
                .filter((item) => item.bundleId !== subscriptionId)
                .some((item) => item.id?.split(".")[0] === TELIA_TRYGG_INDIVIDUAL_OFFERING),
        [cartSubscriptionItems, subscriptionId],
    );

    useEffect(() => {
        dispatch(setTraadlosTabActive(activeBroadbandTab === "FWA_TRADLOS" && activeCategory === "BROADBAND"));
    }, [activeBroadbandTab, activeCategory, dispatch]);

    useEffect(() => {
        if (currentOfferingCode && currentOfferingCode !== selectedOfferingCode) {
            setSelectedOfferingCode(currentOfferingCode ?? undefined);
            const { category, mobileSubTab, broadbandSubTab } = getTabStateForOfferingCode(
                currentOfferingCode,
                allSubscriptions,
            );
            setActiveCategory(category);
            setActiveMobileTab(mobileSubTab);
            setActiveBroadbandTab(broadbandSubTab);
        }
    }, [currentOfferingCode]);

    useEffect(() => {
        onSelectedOfferingCodeChange?.(selectedOfferingCode);
    }, [selectedOfferingCode]);

    // Subscriptions for the selected group, categorized by mobile sub-tab
    const selectedMhsGroup = useMemo(
        () => filteredIcims.mhs.find((g) => g.group === selectedGroup) ?? filteredIcims.mhs[0],
        [filteredIcims.mhs, selectedGroup],
    );
    const selectedMbbGroup = useMemo(
        () => filteredIcims.mbb.find((g) => g.group === selectedGroup) ?? filteredIcims.mbb[0],
        [filteredIcims.mbb, selectedGroup],
    );
    const selectedPrepaidGroup = useMemo(
        () => filteredIcims.prepaid.find((g) => g.group === selectedGroup) ?? filteredIcims.prepaid[0],
        [filteredIcims.prepaid, selectedGroup],
    );
    const selectedVasGroup = useMemo(
        () => filteredIcims.vas.find((g) => g.group === selectedGroup) ?? filteredIcims.vas[0],
        [filteredIcims.vas, selectedGroup],
    );

    // Filter function to check if a subscription is available for existing customer
    const isSubscriptionAvailable = useCallback(
        (sub: RDSubscription): boolean => {
            if (sub.offeringCode === TELIA_TRYGG_INDIVIDUAL_OFFERING && hasTeliaTryggIndividualInCart) {
                return false;
            }

            // For new customers (showOnlyMobileTab is false for new customers unless it's prepaid-only), show all subscriptions
            if (!showOnlyMobileTab || showOnlyPrepaidTab) {
                return true;
            }

            // For existing customers (showOnlyMobileTab is true): if no availability data loaded yet, hide all subscriptions
            // This handles page refresh where data hasn't been fetched yet
            if (availablePricePlans.length === 0) {
                return false;
            }

            // For existing customers with data loaded, check if subscription is in availablePricePlans
            return availablePricePlans.some((p) => p === sub.offeringCode || p.startsWith(sub.offeringCode + "+"));
        },
        [showOnlyMobileTab, showOnlyPrepaidTab, availablePricePlans, hasTeliaTryggIndividualInCart],
    );

    const [
        limitedSubscriptions,
        unlimitedSubscriptions,
        childSubscriptions,
        vasSubscriptions,
        mbbMobileSubscriptions,
        prepaidSubscriptions,
    ] = useMemo(() => {
        const limitedSubs: RDSubscription[] = [];
        const unlimitedSubs: RDSubscription[] = [];
        const childSubs: RDSubscription[] = [];

        (selectedMhsGroup?.subscriptions ?? []).forEach((sub) => {
            if (isZeroPlaySubscription(sub.offeringCode)) return;
            if (!isSubscriptionAvailable(sub)) return; // Filter out unavailable subscriptions
            if (sub.dataUnlimited) {
                unlimitedSubs.push(sub);
            } else if (sub.maxAgeAllowed && sub.maxAgeAllowed <= MAX_AGE_BARN) {
                childSubs.push(sub);
            } else {
                limitedSubs.push(sub);
            }
        });

        const vasSubs = [...(selectedVasGroup?.subscriptions ?? [])]
            .filter(isSubscriptionAvailable)
            .sort((a, b) => a.ranking - b.ranking);
        const mbbSubs = [...(selectedMbbGroup?.subscriptions ?? [])]
            .filter(isSubscriptionAvailable)
            .sort((a, b) => a.ranking - b.ranking);
        const prepaidSubs = [...(selectedPrepaidGroup?.subscriptions ?? [])]
            .filter(isSubscriptionAvailable)
            .sort((a, b) => a.ranking - b.ranking);

        limitedSubs.sort((a, b) => a.ranking - b.ranking);
        unlimitedSubs.sort((a, b) => a.ranking - b.ranking);
        childSubs.sort((a, b) => a.ranking - b.ranking);

        return [limitedSubs, unlimitedSubs, childSubs, vasSubs, mbbSubs, prepaidSubs];
    }, [selectedMhsGroup, selectedMbbGroup, selectedPrepaidGroup, selectedVasGroup, isSubscriptionAvailable]);

    // Check if any subscriptions in a category are available for existing customers
    const hasAvailableSubscriptionsInCategory = useCallback(
        (subs: RDSubscription[]) => {
            // For new customers (showOnlyMobileTab is false for new customers unless it's prepaid-only), show everything
            if (!showOnlyMobileTab || showOnlyPrepaidTab) {
                return true;
            }

            return subs.some(isSubscriptionAvailable);
        },
        [showOnlyMobileTab, showOnlyPrepaidTab, isSubscriptionAvailable],
    );

    // Use ALL subscriptions, not just from selected group
    const showUnlimitedTab = useMemo(() => {
        const unlimitedSubs = allMhsSubscriptions.filter((s) => s.dataUnlimited);
        return hasAvailableSubscriptionsInCategory(unlimitedSubs);
    }, [
        hasAvailableSubscriptionsInCategory,
        allMhsSubscriptions,
        availablePricePlans,
        showOnlyMobileTab,
        showOnlyPrepaidTab,
    ]);

    const showLimitedTab = useMemo(() => {
        const limitedSubs = allMhsSubscriptions.filter(
            (s) => !s.dataUnlimited && !(s.maxAgeAllowed && s.maxAgeAllowed <= MAX_AGE_BARN),
        );
        return hasAvailableSubscriptionsInCategory(limitedSubs);
    }, [
        hasAvailableSubscriptionsInCategory,
        allMhsSubscriptions,
        availablePricePlans,
        showOnlyMobileTab,
        showOnlyPrepaidTab,
    ]);

    const showChildTab = useMemo(() => {
        const childSubs = allMhsSubscriptions.filter((s) => s.maxAgeAllowed && s.maxAgeAllowed <= MAX_AGE_BARN);
        return hasAvailableSubscriptionsInCategory(childSubs);
    }, [
        hasAvailableSubscriptionsInCategory,
        allMhsSubscriptions,
        availablePricePlans,
        showOnlyMobileTab,
        showOnlyPrepaidTab,
    ]);

    const showPrepaidTab = useMemo(() => {
        return hasAvailableSubscriptionsInCategory(allPrepaidSubscriptions);
    }, [
        hasAvailableSubscriptionsInCategory,
        allPrepaidSubscriptions,
        availablePricePlans,
        showOnlyMobileTab,
        showOnlyPrepaidTab,
    ]);

    const getFirstSubscriptionForEffectiveTab = useCallback(
        (category: CategoryTab, subTab: string): RDSubscription | undefined => {
            if (category === "MOBILE") {
                switch (subTab) {
                    case "UNLIMITED":
                        return unlimitedSubscriptions[0];
                    case "LIMITED":
                        return limitedSubscriptions[0];
                    case "CHILD":
                        return childSubscriptions[0];
                    case "PREPAID":
                        return prepaidSubscriptions[0];
                }
            } else if (category === "TJENESTER") {
                return vasSubscriptions[0];
            } else if (category === "BROADBAND") {
                return mbbMobileSubscriptions[0];
            }
            return undefined;
        },
        [
            limitedSubscriptions,
            unlimitedSubscriptions,
            childSubscriptions,
            vasSubscriptions,
            mbbMobileSubscriptions,
            prepaidSubscriptions,
        ],
    );

    useEffect(() => {
        if (!showOnlyMobileTab) return;
        if (showOnlyPrepaidTab) return;

        setActiveCategory("MOBILE");

        if (
            mobileOfferingCodes.length > 0 &&
            !existingSubscriptionForChange &&
            (!selectedOfferingCode || !mobileOfferingCodes.includes(selectedOfferingCode))
        ) {
            setSelectedOfferingCode(mobileOfferingCodes[0]);
        }
    }, [showOnlyMobileTab, mobileOfferingCodes, selectedOfferingCode]);

    useEffect(() => {
        if (!showOnlyPrepaidTab) return;
        setActiveCategory("MOBILE");
        setActiveMobileTab("PREPAID");
        if (!currentOfferingCode) {
            setSelectedOfferingCode(undefined);
        }
    }, [showOnlyPrepaidTab]);

    // Switch to available tab if current tab is not available for existing customer
    useEffect(() => {
        if (!existingSubscriptionForChange || !availablePricePlans.length) return;

        const isCurrentTabAvailable = () => {
            switch (activeMobileTab) {
                case "UNLIMITED":
                    return showUnlimitedTab;
                case "LIMITED":
                    return showLimitedTab;
                case "CHILD":
                    return showChildTab;
                case "PREPAID":
                    return showPrepaidTab;
                default:
                    return true;
            }
        };

        if (!isCurrentTabAvailable()) {
            // Switch to first available tab
            if (showUnlimitedTab) {
                setActiveMobileTab("UNLIMITED");
            } else if (showLimitedTab) {
                setActiveMobileTab("LIMITED");
            } else if (showChildTab) {
                setActiveMobileTab("CHILD");
            } else if (showPrepaidTab) {
                setActiveMobileTab("PREPAID");
            }
        }
    }, [
        existingSubscriptionForChange,
        availablePricePlans.length,
        activeMobileTab,
        showUnlimitedTab,
        showLimitedTab,
        showChildTab,
        showPrepaidTab,
    ]);

    // Clear validation error when selection changes
    useEffect(() => {
        if (validationError) {
            setValidationError("");
        }
        // Update credit check info when selection changes
        isSelectedOfferingValid();
    }, [selectedOfferingCode]);

    const selectedSubscription = useMemo(
        () =>
            [...allMhsSubscriptions, ...allMbbSubscriptions, ...allPrepaidSubscriptions].find(
                (sub) => `${sub.offeringCode}.${sub.icim}` === selectedOfferingCode,
            ),
        [allMhsSubscriptions, allMbbSubscriptions, allPrepaidSubscriptions, selectedOfferingCode],
    );

    const existingProductUserAge = useMemo(() => {
        const birthDate = existingSubscriptionForChange?.birthDate;
        if (!birthDate) return undefined;

        // birthDate is expected to be YYYY-MM-DD; parse as a local date to avoid timezone shifting.
        const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(birthDate);
        if (!match) return undefined;
        const [, y, m, d] = match;
        const birth = new Date(Number(y), Number(m) - 1, Number(d));
        if (Number.isNaN(birth.getTime())) return undefined;

        const today = new Date();
        let age = today.getFullYear() - birth.getFullYear();
        const hasHadBirthdayThisYear =
            today.getMonth() > birth.getMonth() ||
            (today.getMonth() === birth.getMonth() && today.getDate() >= birth.getDate());
        if (!hasHadBirthdayThisYear) age--;
        return age;
    }, [existingSubscriptionForChange?.birthDate]);

    const existingForChangeDisplay = useMemo(() => {
        if (!existingSubscriptionForChange?.offeringCode) return undefined;
        const code = existingSubscriptionForChange.offeringCode;
        const name = existingSubscriptionForChange.offeringName ?? code;
        return { code, name };
    }, [existingSubscriptionForChange]);

    // Reset selected phone number if user switches to a different type of subscription
    const maybeResetSelectedPhoneNumber = async () => {
        if (!selectedOfferingCode) return;

        const getSubscriptionCategory = (code: string) => {
            if (isBroadbandSubscription(code)) return "BROADBAND";
            if (isZeroPlaySubscription(code)) return "SERVICE";
            return "MOBILE";
        };
        const previousCategory = currentOfferingCode ? getSubscriptionCategory(currentOfferingCode) : undefined;
        const newCategory = getSubscriptionCategory(selectedOfferingCode);
        const hasChangedCategory = previousCategory !== undefined && previousCategory !== newCategory;

        if (hasChangedCategory && selectedPanel === CUSTOMER_TYPE.NEW) {
            dispatch(setSelectedPhoneNumber("", CUSTOMER_TYPE.EXISTING, subscriptionId));
            dispatch({
                type: RETAILER_CHOOSE_PHONE_NUMBER_SWITCH_PANEL,
                activePanel: CUSTOMER_TYPE.EXISTING,
            });

            await dispatch(fetchAvailableNumbers(false, subscriptionId ?? ""));
        }
    };

    const updateSubscriptionAndPhoneNumber = async (cartItemId = subscriptionId): Promise<boolean> => {
        if (selectedOfferingCode) {
            await dispatch(
                addExistingSubscriptionSkeletonToCart({
                    phoneNumber: existingSubscriptionForChange?.phoneNumber ?? "",
                    scope: existingSubscriptionForChange?.changeScope ?? "",
                    offeringCode: selectedOfferingCode,
                    cartItemId,
                }),
            );
            if (existingSubscriptionForChange?.phoneNumber) {
                // Set the phone number in UI form state
                dispatch(
                    setSelectedPhoneNumber(
                        existingSubscriptionForChange.phoneNumber,
                        CUSTOMER_TYPE.EXISTING,
                        cartItemId,
                        true,
                    ),
                );
                // Set correct phone number panel in UI
                dispatch({
                    type: RETAILER_CHOOSE_PHONE_NUMBER_SWITCH_PANEL,
                    activePanel: CUSTOMER_TYPE.EXISTING,
                });

                const checkResult = (await dispatch(
                    checkCustomerPhoneNumber(
                        existingSubscriptionForChange.phoneNumber,
                        cartItemId,
                        CUSTOMER_TYPE.EXISTING,
                        false,
                        "subscriptionChange",
                    ),
                )) as PhoneCheckResult | undefined;

                if (!checkResult?.allowedToProceed) {
                    const alertMessage =
                        checkResult?.messages?.find((m) => m.type === "alert")?.text ??
                        "Kan ikke fortsette med valgt abonnement.";
                    setValidationError(alertMessage);
                    return false;
                }
            }

            await maybeResetSelectedPhoneNumber();
        }
        return true;
    };

    const completeSubscriptionChange = () => {
        toast.success("Abonnementet er endret", { position: "top-center" });
        dispatch(resetCustomerInfo());
        dispatch(setActiveSubscription());
        dispatch(setIsAddingSubscription(false));
        dispatch(clearExistingSubscriptionForChange());
        dispatch(setExistingSubscriptionForChange(undefined));
    };

    // Validate if selected offering is allowed for existing customer
    const isSelectedOfferingValid = () => {
        // Skip validation if no availability data or not existing customer
        if (!availablePricePlans.length || !existingSubscriptionForChange) {
            return { valid: true, message: "" };
        }

        if (!selectedOfferingCode) {
            return { valid: false, message: "Vennligst velg et abonnement" };
        }

        const [selectedPlan, selectedIcim] = selectedOfferingCode.split(".");

        // Check if price plan is allowed (only show what's in availablePricePlans)
        const isPlanAllowed = availablePricePlans.some((p) => p === selectedPlan || p.startsWith(selectedPlan + "+"));

        if (!isPlanAllowed) {
            return {
                valid: false,
                message: `Abonnementet "${selectedPlan}" er ikke tilgjengelig for denne kunden. Vennligst velg et annet abonnement.`,
            };
        }

        // Check if ICIM is allowed (only show what's in availableIcims)
        const hasIcimData = Object.keys(availableIcims).length > 0;
        if (hasIcimData) {
            const matchingKeys = Object.keys(availableIcims).filter(
                (k) => k === selectedPlan || k.startsWith(selectedPlan + "+"),
            );

            // If no ICIM data for this plan, allow it
            if (matchingKeys.length === 0) {
                return { valid: true, message: "" };
            }

            const allowedIcims = new Set(matchingKeys.flatMap((k) => availableIcims[k]));
            if (!allowedIcims.has(selectedIcim)) {
                if (selectedIcim !== "REGULAR") {
                    return {
                        valid: false,
                        message: `Kan ikke velge dette abonnementet med ny bindingsperiode. Dette er vanligvis fordi abonnementet allerede har en binding som ikke kan brytes ennå.`,
                    };
                } else if (
                    existingSubscriptionForChange?.offeringCode &&
                    selectedOfferingCode === existingSubscriptionForChange.offeringCode
                ) {
                    return {
                        valid: false,
                        message: "Kan ikke velge samme abonnement og bindingsperiode som kunden allerede har",
                    };
                } else {
                    return {
                        valid: false,
                        message: `Kan ikke velge dette abonnementet med valgt bindingsperiode.`,
                    };
                }
            }
        }

        return { valid: true, message: "" };
    };

    const handleGroupChange = (value: string, firstOfferingCode: string | undefined) => {
        // Hardcoding special case since we have two Telia X kampanje subscriptions
        if ((selectedGroup === "simdeal" && value === "lease") || (selectedGroup === "lease" && value === "simdeal")) {
            if (selectedOfferingCode?.includes("SUMMER")) {
                setSelectedGroup(value as SubscriptionGroup);
                setSelectedOfferingCode(`${firstOfferingCode}_SUMMER`);
                return;
            }
        }
        setSelectedGroup(value as SubscriptionGroup);
        setSelectedOfferingCode(firstOfferingCode);
    };

    const handleCategoryChange = (newCategory: string) => {
        const cat = newCategory as CategoryTab;
        setActiveCategory(cat);
        const preferredGroup = getInitialSubscriptionGroup(
            { category: cat, mobileSubTab: activeMobileTab },
            filteredIcims,
            DEFAULT_SUBSCRIPTION_GROUP,
        );
        setSelectedGroup(preferredGroup);
        // Use filteredIcims to ensure we only select available subscriptions
        const { offeringCode, resetMobileTab, resetBroadbandTab } = getInitialOfferingCodeForCategory(
            cat,
            filteredIcims,
            activeMobileTab,
            activeBroadbandTab,
            preferredGroup,
        );
        if (resetMobileTab) setActiveMobileTab(resetMobileTab);
        if (resetBroadbandTab) setActiveBroadbandTab(resetBroadbandTab);
        setSelectedOfferingCode(offeringCode);
    };

    const handleMobileTabChange = (newTab: string) => {
        const tab = newTab as MobileSubTab;
        setActiveMobileTab(tab);

        // PREPAID uses its own icim list; all other mobile tabs use mhs
        // Use filteredIcims to ensure we only select from available subscriptions
        const isPrepaid = tab === "PREPAID";
        const icimList = isPrepaid ? filteredIcims.prepaid : filteredIcims.mhs;
        const resolvedGroup =
            icimList.find((g) => g.group === selectedGroup) ??
            icimList.find((g) => g.group === "regular") ??
            icimList[0];
        const resolvedGroupName = resolvedGroup?.group ?? "regular";
        setSelectedGroup(resolvedGroupName);

        // Derive first subscription from filteredIcims to ensure it's available
        const subs = resolvedGroup?.subscriptions ?? [];
        const firstSub = getFirstSubscriptionForMobileSubTab(subs, tab);

        setSelectedOfferingCode(firstSub ? `${firstSub.offeringCode}.${firstSub.icim}` : undefined);
    };

    const visibleExistingAccounts = existingAccounts.filter(
        (acc) => acc.accountId !== NEW_ACCOUNT_VALUE && acc.subscriptions.length > 0,
    );
    const showMultipleAccountsInfo =
        !existingForChangeDisplay &&
        (activeCategory === "MOBILE" || activeCategory === "TJENESTER") &&
        visibleExistingAccounts.length > 1 &&
        !visibleExistingAccounts.every((acc) => {
            const additionalForSelectedAccount = selectedAccountId && acc.accountId === selectedAccountId ? 1 : 0;
            return (
                acc.subscriptions.filter(isCountableSubscription).length + additionalForSelectedAccount >=
                MAX_NUMBER_OF_ALLOWED_SUBSCRIPTIONS
            );
        }) &&
        !!selectedAccountId &&
        selectedAccountId !== NEW_ACCOUNT_VALUE;

    const isSelectedAccountFull = useMemo(() => {
        if (!selectedAccountId || selectedAccountId === NEW_ACCOUNT_VALUE || existingForChangeDisplay) return false;
        const selectedAccount = existingAccounts.find(
            (acc) =>
                acc.accountId !== NEW_ACCOUNT_VALUE &&
                acc.subscriptions.length > 0 &&
                acc.accountId === selectedAccountId,
        );
        if (!selectedAccount) return false;
        const existingPhoneNumbers = new Set(selectedAccount.subscriptions.map((sub) => sub.phoneNumber));
        const newAdditions = Object.values(checkoutSubscriptionsState).filter((sub) => {
            if (isNonMhsSubscription(sub.code)) return false;
            // Exclude the subscription currently being configured — it is already allocated
            // and should not push the account over the limit while the user edits it.
            if (sub.id === subscriptionId) return false;
            const phone = sub.phoneNumber?.selectedPhoneNumber;
            // Subscriptions without a phone number haven't been assigned to any account yet
            if (!phone) return false;
            if (existingPhoneNumbers.has(phone)) return false;
            const targetAccountId = sub.phoneNumber?.accountId ?? selectedAccountId;
            return targetAccountId === selectedAccountId;
        }).length;
        return (
            selectedAccount.subscriptions.filter(isCountableSubscription).length + newAdditions >=
            MAX_NUMBER_OF_ALLOWED_SUBSCRIPTIONS
        );
    }, [selectedAccountId, existingAccounts, checkoutSubscriptionsState, existingForChangeDisplay, subscriptionId]);

    const existingOfferingCodeIsSellable = useMemo(() => {
        if (!existingForChangeDisplay) return true;
        // Use unfiltered icims — filtering by availablePricePlans is for display only,
        // not for determining whether the existing subscription is still sold.
        const allUnfilteredMhs = icims.mhs.flatMap((g) => g.subscriptions);
        const allUnfilteredMbb = icims.mbb.flatMap((g) => g.subscriptions);
        const allUnfilteredVas = icims.vas.flatMap((g) => g.subscriptions);
        const allUnfilteredPrepaid = icims.prepaid.flatMap((g) => g.subscriptions);
        const categorySubscriptions = (() => {
            if (activeCategory === "TJENESTER") return allUnfilteredVas;
            if (activeCategory === "BROADBAND") return allUnfilteredMbb;
            return [...allUnfilteredMhs, ...allUnfilteredPrepaid];
        })();
        return categorySubscriptions.some((sub) => `${sub.offeringCode}.${sub.icim}` === existingForChangeDisplay.code);
    }, [existingForChangeDisplay, activeCategory, icims]);

    const isExistingPuAgeAllowed =
        existingProductUserAge === undefined ||
        selectedSubscription?.maxAgeAllowed === undefined ||
        existingProductUserAge < selectedSubscription.maxAgeAllowed;

    return (
        <div className="choose-subscription">
            {showMultipleAccountsInfo && (
                <Notification type="tile" variant="information" mb="200">
                    <Notification.Icon />
                    <Notification.Text>
                        Kunden har flere avtaler. Nytt abonnement legges på fakturanr. {selectedAccountId}. Du kan endre
                        til en annen nederst på siden.
                    </Notification.Text>
                </Notification>
            )}
            {!!breakageFeeAmount && (
                <Notification type="tile" variant="information" mb="200">
                    <Notification.Icon />
                    <Notification.Text>
                        Et eventuelt bruddgebyr vil bli på {breakageFeeAmount},- og dette vil vises i handlekurv hvis
                        valgt abonnement krever dette.
                    </Notification.Text>
                </Notification>
            )}
            {existingForChangeDisplay && !existingOfferingCodeIsSellable && (
                <Notification type="tile" variant="information" mb="200">
                    <Notification.Icon />
                    <Notification.Text>
                        Obs! Eksisterende abonnement (<i>{existingForChangeDisplay.name} med gammel prisplan</i>) selges
                        ikke lenger. Vennligst velg et abonnement under for å fortsette.
                    </Notification.Text>
                </Notification>
            )}
            {validationError && (
                <Notification type="tile" variant="warning" mb="200">
                    <Notification.Icon />
                    <Notification.Text>{validationError}</Notification.Text>
                </Notification>
            )}
            {(showCreditCheckInfo || showExistingCustomerNewSubscriptionCreditCheckInfo) && (
                <Notification type="tile" variant="information" mb="200">
                    <Notification.Icon name="info" />
                    <Notification.Text>
                        {showExistingCustomerNewSubscriptionCreditCheckInfo ? (
                            <Text>Ved nytt abonnement vil det bli gjennomført en kredittsjekk av kunden</Text>
                        ) : (
                            <Text>Ved telefonbinding vil det bli gjennomført en kredittsjekk av kunden</Text>
                        )}
                    </Notification.Text>
                </Notification>
            )}
            <Box mb="200">
                <Tabs value={activeCategory} onValueChange={handleCategoryChange}>
                    <Tabs.List className="choose-subscription__tabs-list">
                        {!showOnlyMobileTab && <Tabs.Trigger value="MOBILE">Mobilabonnement</Tabs.Trigger>}
                        {!showOnlyMobileTab && <Tabs.Trigger value="TJENESTER">Telia Trygg</Tabs.Trigger>}
                        {!showOnlyMobileTab && <Tabs.Trigger value="BROADBAND">Bredbånd</Tabs.Trigger>}
                        {/* <Tabs.Trigger value="TV">TV</Tabs.Trigger> */}
                    </Tabs.List>
                    <Box mt="200">
                        <Tabs.Content value="MOBILE">
                            <Tabs value={activeMobileTab} onValueChange={handleMobileTabChange}>
                                <Tabs.List className="choose-subscription__tabs-list choose-subscription__tabs-list--sub">
                                    {!showOnlyPrepaidTab && showUnlimitedTab && (
                                        <Tabs.Trigger value="UNLIMITED">Ubegrenset</Tabs.Trigger>
                                    )}
                                    {!showOnlyPrepaidTab && showLimitedTab && (
                                        <Tabs.Trigger value="LIMITED">Fast</Tabs.Trigger>
                                    )}
                                    {!showOnlyPrepaidTab && showChildTab && (
                                        <Tabs.Trigger value="CHILD">Barn</Tabs.Trigger>
                                    )}
                                    {showPrepaidTab && <Tabs.Trigger value="PREPAID">Kontant</Tabs.Trigger>}
                                </Tabs.List>
                                <Box mt="200">
                                    <Tabs.Content value="UNLIMITED">
                                        {existingProductUserAge !== undefined &&
                                            existingProductUserAge >=
                                                (selectedSubscription?.maxAgeAllowed ?? Number.POSITIVE_INFINITY) && (
                                                <Notification type="tile" variant="warning" mb="200">
                                                    <Notification.Icon />
                                                    <Notification.Text>
                                                        Nåværende bruker er over {existingProductUserAge} år og har
                                                        passert alderskravet for dette abonnementet (maks{" "}
                                                        {selectedSubscription?.maxAgeAllowed} år).
                                                    </Notification.Text>
                                                </Notification>
                                            )}
                                        {availablePricePlans.length > 0 &&
                                        !allMhsSubscriptions.some((s) => s.dataUnlimited) ? (
                                            <Notification type="tile" variant="information" mb="200">
                                                <Notification.Icon />
                                                <Notification.Text>
                                                    Kan ikke endres til UBEGRENSET-abonnement
                                                </Notification.Text>
                                            </Notification>
                                        ) : (
                                            <>
                                                <SubscriptionSelect
                                                    name="choose-subscription-unlimited"
                                                    subscriptions={unlimitedSubscriptions}
                                                    selectedOfferingCode={selectedOfferingCode}
                                                    existingForChangeDisplay={existingForChangeDisplay}
                                                    offeringCodes={offeringCodesInCartAndAccount}
                                                    onValueChange={setSelectedOfferingCode}
                                                    isActive={
                                                        activeCategory === "MOBILE" && activeMobileTab === "UNLIMITED"
                                                    }
                                                />
                                                <GroupSelect
                                                    selectableSubscriptions={unlimitedSubscriptions}
                                                    selectedOfferingCode={selectedOfferingCode}
                                                    name="choose-subscription-unlimited-campaign"
                                                    icimGroups={filteredIcims.mhs}
                                                    selectedGroup={selectedGroup}
                                                    selectedPricePlan={selectedPricePlan}
                                                    onGroupChange={handleGroupChange}
                                                />
                                            </>
                                        )}
                                    </Tabs.Content>
                                    <Tabs.Content value="LIMITED">
                                        {existingProductUserAge !== undefined &&
                                            selectedSubscription?.maxAgeAllowed &&
                                            existingProductUserAge >= selectedSubscription.maxAgeAllowed && (
                                                <Notification type="tile" variant="warning" mb="200">
                                                    <Notification.Icon />
                                                    <Notification.Text>
                                                        Nåværende bruker er over {existingProductUserAge} år og har
                                                        passert alderskravet for dette abonnementet (maks{" "}
                                                        {selectedSubscription.maxAgeAllowed} år).
                                                    </Notification.Text>
                                                </Notification>
                                            )}
                                        {availablePricePlans.length > 0 &&
                                        !allMhsSubscriptions.some(
                                            (s) =>
                                                !s.dataUnlimited &&
                                                !(s.maxAgeAllowed && s.maxAgeAllowed <= MAX_AGE_BARN),
                                        ) ? (
                                            <Notification type="tile" variant="information" mb="200">
                                                <Notification.Icon />
                                                <Notification.Text>
                                                    Kan ikke endres til FAST-abonnement
                                                </Notification.Text>
                                            </Notification>
                                        ) : (
                                            <>
                                                <SubscriptionSelect
                                                    name="choose-subscription-limited"
                                                    subscriptions={limitedSubscriptions}
                                                    selectedOfferingCode={selectedOfferingCode}
                                                    existingForChangeDisplay={existingForChangeDisplay}
                                                    offeringCodes={offeringCodesInCartAndAccount}
                                                    onValueChange={setSelectedOfferingCode}
                                                    isActive={
                                                        activeCategory === "MOBILE" && activeMobileTab === "LIMITED"
                                                    }
                                                />
                                                <GroupSelect
                                                    selectableSubscriptions={limitedSubscriptions}
                                                    selectedOfferingCode={selectedOfferingCode}
                                                    name="choose-subscription-limited-campaign"
                                                    icimGroups={filteredIcims.mhs}
                                                    selectedGroup={selectedGroup}
                                                    selectedPricePlan={selectedPricePlan}
                                                    findFallbackSub={(subs) =>
                                                        subs.find(
                                                            (s) =>
                                                                !s.dataUnlimited &&
                                                                !(s.maxAgeAllowed && s.maxAgeAllowed <= MAX_AGE_BARN),
                                                        ) ?? subs[0]
                                                    }
                                                    onGroupChange={handleGroupChange}
                                                />
                                            </>
                                        )}
                                    </Tabs.Content>
                                    <Tabs.Content value="CHILD">
                                        {!existingSubscriptionForChange?.birthDate && (
                                            <Notification type="tile" variant="information" mb="200">
                                                <Notification.Icon />
                                                <Notification.Text>
                                                    For å kjøpe BARN-abonnement må bruker være under 16 år.
                                                </Notification.Text>
                                            </Notification>
                                        )}
                                        {existingProductUserAge !== undefined &&
                                            existingProductUserAge >
                                                (selectedSubscription?.maxAgeAllowed ?? MAX_AGE_BARN) && (
                                                <Notification type="tile" variant="warning" mb="200">
                                                    <Notification.Icon />
                                                    <Notification.Text>
                                                        Nåværende bruker er over {existingProductUserAge} år og
                                                        oppfyller ikke alderskravet for dette abonnementet (maks{" "}
                                                        {selectedSubscription?.maxAgeAllowed ?? MAX_AGE_BARN} år).
                                                    </Notification.Text>
                                                </Notification>
                                            )}
                                        {availablePricePlans.length > 0 &&
                                        !allMhsSubscriptions.some(
                                            (s) => s.maxAgeAllowed && s.maxAgeAllowed <= MAX_AGE_BARN,
                                        ) ? (
                                            <Notification type="tile" variant="information" mb="200">
                                                <Notification.Icon />
                                                <Notification.Text>
                                                    Kan ikke endres til BARN-abonnement
                                                </Notification.Text>
                                            </Notification>
                                        ) : (
                                            <>
                                                <SubscriptionSelect
                                                    name="choose-subscription-child"
                                                    subscriptions={childSubscriptions}
                                                    selectedOfferingCode={selectedOfferingCode}
                                                    existingForChangeDisplay={existingForChangeDisplay}
                                                    offeringCodes={offeringCodesInCartAndAccount}
                                                    onValueChange={setSelectedOfferingCode}
                                                    isActive={
                                                        activeCategory === "MOBILE" && activeMobileTab === "CHILD"
                                                    }
                                                />
                                                <GroupSelect
                                                    selectableSubscriptions={childSubscriptions}
                                                    selectedOfferingCode={selectedOfferingCode}
                                                    name="choose-subscription-child-campaign"
                                                    icimGroups={filteredIcims.mhs}
                                                    selectedGroup={selectedGroup}
                                                    selectedPricePlan={selectedPricePlan}
                                                    findFallbackSub={(subs) =>
                                                        subs.find(
                                                            (s) => s.maxAgeAllowed && s.maxAgeAllowed <= MAX_AGE_BARN,
                                                        ) ?? subs[0]
                                                    }
                                                    onGroupChange={handleGroupChange}
                                                />
                                            </>
                                        )}
                                    </Tabs.Content>
                                    <Tabs.Content value="PREPAID">
                                        {availablePricePlans.length > 0 && allPrepaidSubscriptions.length === 0 ? (
                                            <Notification type="tile" variant="information" mb="200">
                                                <Notification.Icon />
                                                <Notification.Text>Kan ikke endres til Kontant</Notification.Text>
                                            </Notification>
                                        ) : (
                                            <>
                                                <SubscriptionSelect
                                                    name="choose-subscription-prepaid"
                                                    subscriptions={prepaidSubscriptions}
                                                    selectedOfferingCode={selectedOfferingCode}
                                                    existingForChangeDisplay={existingForChangeDisplay}
                                                    offeringCodes={offeringCodesInCartAndAccount}
                                                    onValueChange={setSelectedOfferingCode}
                                                    isActive={
                                                        activeCategory === "MOBILE" && activeMobileTab === "PREPAID"
                                                    }
                                                />
                                                <GroupSelect
                                                    selectableSubscriptions={prepaidSubscriptions}
                                                    selectedOfferingCode={selectedOfferingCode}
                                                    name="choose-subscription-prepaid-campaign"
                                                    icimGroups={filteredIcims.prepaid}
                                                    selectedGroup={selectedGroup}
                                                    selectedPricePlan={selectedPricePlan}
                                                    onGroupChange={handleGroupChange}
                                                />
                                            </>
                                        )}
                                        {selectedOfferingCode && isPrepaidSubscription(selectedOfferingCode) && (
                                            <Box mt="200">
                                                <Checkbox>
                                                    <Checkbox.Trigger
                                                        checked={
                                                            subscriptionId
                                                                ? (checkoutSubscriptions.find(
                                                                      (s) => s.id === subscriptionId,
                                                                  )?.isPrepaidWithStartpakke ?? false)
                                                                : localIsPrepaidWithStartpakke
                                                        }
                                                        onCheckedChange={(checked) => {
                                                            if (subscriptionId) {
                                                                dispatch(
                                                                    setPrepaidWithStartpakke(subscriptionId, !!checked),
                                                                );
                                                            } else {
                                                                setLocalIsPrepaidWithStartpakke(!!checked);
                                                            }
                                                        }}
                                                    />
                                                    <span>Med Startpakke (som er forhåndsaktivert)</span>
                                                </Checkbox>
                                            </Box>
                                        )}
                                    </Tabs.Content>
                                </Box>
                            </Tabs>
                        </Tabs.Content>
                        <Tabs.Content value="TJENESTER">
                            <Tabs
                                value={activeTjenesteTab}
                                onValueChange={(newTab) => {
                                    setActiveTjenesteTab(newTab as TjenesteSubTab);
                                    const firstSub = getFirstSubscriptionForEffectiveTab("TJENESTER", newTab);
                                    setSelectedOfferingCode(
                                        firstSub ? `${firstSub.offeringCode}.${firstSub.icim}` : undefined,
                                    );
                                }}
                            >
                                {/* Todo: Uncomment when we get more services in summer */}
                                {/* <Tabs.List className="choose-subscription__tabs-list choose-subscription__tabs-list--sub">
                                    <Tabs.Trigger value="TRYGGHET">Telia Trygg</Tabs.Trigger>
                                </Tabs.List> */}
                                <Box mt="200">
                                    <Tabs.Content value="TRYGGHET">
                                        <SubscriptionSelect
                                            name="choose-subscription-vas"
                                            subscriptions={vasSubscriptions}
                                            selectedOfferingCode={selectedOfferingCode}
                                            existingForChangeDisplay={existingForChangeDisplay}
                                            offeringCodes={offeringCodesInCartAndAccount}
                                            onValueChange={setSelectedOfferingCode}
                                            isActive={activeCategory === "TJENESTER"}
                                        />
                                        <GroupSelect
                                            selectableSubscriptions={vasSubscriptions}
                                            selectedOfferingCode={selectedOfferingCode}
                                            name="choose-subscription-vas-campaign"
                                            icimGroups={filteredIcims.vas}
                                            selectedGroup={selectedGroup}
                                            selectedPricePlan={selectedPricePlan}
                                            onGroupChange={handleGroupChange}
                                        />
                                    </Tabs.Content>
                                </Box>
                            </Tabs>
                        </Tabs.Content>
                        <Tabs.Content value="BROADBAND">
                            <Tabs
                                value={activeBroadbandTab}
                                onValueChange={(newTab) => {
                                    const tab = newTab as BroadbandSubTab;
                                    setActiveBroadbandTab(tab);
                                    if (tab !== "FWA_TRADLOS") {
                                        const firstSub = getFirstSubscriptionForEffectiveTab("BROADBAND", tab);
                                        setSelectedOfferingCode(
                                            firstSub ? `${firstSub.offeringCode}.${firstSub.icim}` : undefined,
                                        );
                                    }
                                }}
                            >
                                <Tabs.List className="choose-subscription__tabs-list choose-subscription__tabs-list--sub">
                                    {/* TODO: Uncomment when we get more options */}
                                    {/* <Tabs.Trigger value="MBB_MOBIL">Mobilt bredbånd</Tabs.Trigger> */}
                                    {/* <Tabs.Trigger value="MBB_TRADLOS">Trådløst bredbånd</Tabs.Trigger> */}
                                </Tabs.List>
                                <Box mt="200">
                                    <Tabs.Content value="MBB_MOBIL">
                                        <SubscriptionSelect
                                            name="choose-subscription-mbb-mobil"
                                            subscriptions={mbbMobileSubscriptions}
                                            selectedOfferingCode={selectedOfferingCode}
                                            existingForChangeDisplay={existingForChangeDisplay}
                                            offeringCodes={offeringCodesInCartAndAccount}
                                            onValueChange={setSelectedOfferingCode}
                                            isActive={
                                                activeCategory === "BROADBAND" && activeBroadbandTab === "MBB_MOBIL"
                                            }
                                        />
                                        <GroupSelect
                                            selectableSubscriptions={mbbMobileSubscriptions}
                                            selectedOfferingCode={selectedOfferingCode}
                                            name="choose-subscription-mbb-mobil-campaign"
                                            icimGroups={filteredIcims.mbb}
                                            selectedGroup={selectedGroup}
                                            selectedPricePlan={selectedPricePlan}
                                            onGroupChange={handleGroupChange}
                                        />
                                    </Tabs.Content>
                                    <Tabs.Content value="FWA_TRADLOS">
                                        <TradlosAddressSearch />
                                    </Tabs.Content>
                                </Box>
                            </Tabs>
                        </Tabs.Content>
                        <Tabs.Content value="TV">
                            <Tabs
                                value={activeTvTab}
                                onValueChange={(newTab) => {
                                    setActiveTvTab(newTab as TvSubTab);
                                    setSelectedOfferingCode(undefined);
                                }}
                            >
                                <Tabs.List className="choose-subscription__tabs-list choose-subscription__tabs-list--sub">
                                    <Tabs.Trigger value="TV_BOX">TeliaPlay med TV-boks</Tabs.Trigger>
                                    <Tabs.Trigger value="TV_NO_BOX">TeliaPlay uten TV-boks</Tabs.Trigger>
                                </Tabs.List>
                                <Box mt="200">
                                    <Notification type="tile" variant="information">
                                        <Notification.Icon />
                                        <Notification.Text>Abonnement vil bli tilgjengelig snart.</Notification.Text>
                                    </Notification>
                                </Box>
                            </Tabs>
                        </Tabs.Content>
                    </Box>
                </Tabs>
            </Box>
            {selectedSubscription && !isTraadlosTabActive && <SubscriptionInfo subscription={selectedSubscription} />}
            <SubscriptionStepsControls
                isProcessing={isProcessing}
                hideBack
                primaryDisabled={
                    isTraadlosTabActive
                        ? fwaSelectedPackageId === null
                        : !selectedOfferingCode ||
                          (!existingOfferingCodeIsSellable &&
                              existingForChangeDisplay &&
                              existingForChangeDisplay.code === selectedOfferingCode) ||
                          BLOCKED_SIM_ONLY_OFFERING_CODES.includes(selectedOfferingCode as OfferingCode) ||
                          isSelectedAccountFull ||
                          (isSubscriptionChangeScopeChange && !isExistingPuAgeAllowed)
                }
                primaryTooltip={isSelectedAccountFull ? "Valgt faktura er full, vennligst velg en annen." : undefined}
                onPrimaryClick={async () => {
                    // Clear previous validation error
                    setValidationError("");

                    if (isTraadlosTabActive) {
                        if (fwaSelectedPackageId === null || !fwaSelectedPackage) return;
                        await saveFwaPackageToOrder(fwaSelectedPackage);
                        window.location.assign(PAGES.RETAILER.ORDER.SUMMARY.URL);
                        return;
                    }

                    // Retrieve loan limits before choosing hardware financing for Telefonbinding.
                    const isExistingCustomer = existingAccounts.length > 0;
                    const isCreditCheckPerformed = creditCheck?.performed || false;
                    const isAddingNewSubscription = isInAddSubscriptionFlow && !existingSubscriptionForChange;
                    const shouldForceCreditCheck =
                        isExistingCustomer &&
                        !isCreditCheckPerformed &&
                        !isPrepaid &&
                        !isZeroPlaySubscription(selectedOfferingCode) &&
                        !isPrepaidSubscription(selectedOfferingCode) &&
                        (isSelectedOfferingWithLease || isAddingNewSubscription);

                    if (shouldForceCreditCheck) {
                        try {
                            await dispatch(handleCreditCheck(true));
                        } catch (error: any) {
                            Sentry.captureException(error, (scope) => {
                                scope.setTag("errorType", "retailer-credit-check");
                                scope.setExtra("selectedOfferingCode", selectedOfferingCode ?? null);
                                scope.setExtra("currentOfferingCode", currentOfferingCode ?? null);
                                return scope;
                            });
                            setValidationError(
                                error?.displayable ? error.error : "Kredittsjekk feilet. Vennligst prøv igjen.",
                            );
                            return;
                        }
                    }

                    // Validate selection for existing customers
                    const validation = isSelectedOfferingValid();
                    if (!validation.valid) {
                        setValidationError(validation.message);
                        return;
                    }

                    if (isSubscriptionChangeScopeChange && subscriptionId) {
                        const allowed = await updateSubscriptionAndPhoneNumber();
                        if (!allowed) return;
                        completeSubscriptionChange();

                        return;
                    }

                    if (isSubscriptionChangeScopeChange && selectedOfferingCode) {
                        const newCartItemId = await dispatch(
                            addSubscriptionChangeToCart(selectedOfferingCode as OfferingCode),
                        );
                        if (!newCartItemId) return;
                        // Removing a cart item also clears existingSubscriptionForChange, so restore it.
                        const rollbackCartItem = async () => {
                            await dispatch(removeItemByIdFromCart(newCartItemId, false));
                            await dispatch(setExistingSubscriptionForChange(existingSubscriptionForChange));
                        };
                        let allowed = false;
                        try {
                            allowed = await updateSubscriptionAndPhoneNumber(newCartItemId);
                        } catch {
                            await rollbackCartItem();
                            setValidationError("Kunne ikke endre abonnementet. Vennligst prøv igjen.");
                            return;
                        }
                        if (!allowed) {
                            await rollbackCartItem();
                            return;
                        }
                        completeSubscriptionChange();

                        return;
                    }

                    const checkoutSubscription = checkoutSubscriptions.find((s) => s.id === subscriptionId);
                    const hasCompletedFlowForSubscription = !!checkoutSubscription?.productUser;

                    if (selectedOfferingCode !== currentOfferingCode || !hasCompletedFlowForSubscription) {
                        dispatch(setValue("isEmailTakenForTeliaTrygg", false));
                        dispatch(setValue("isEmailTakenForTeliaSky", false));
                        if (!subscriptionId) {
                            if (existingSubscriptionForChange) {
                                await dispatch(setExistingSubscriptionForChange(undefined));
                            }
                            const response = await dispatch(
                                addExistingSubscriptionSkeletonToCart({
                                    phoneNumber: existingSubscriptionForChange?.phoneNumber ?? "",
                                    scope: existingSubscriptionForChange?.changeScope ?? "",
                                    offeringCode: selectedOfferingCode,
                                }),
                            );

                            const newSubscriptionId = response.cartItemId;
                            if (!newSubscriptionId) return;
                            dispatch(setActiveSubscription(newSubscriptionId));
                            dispatch(setSubscriptionStep(newSubscriptionId, 0));
                            dispatch(prefillCustomerForm(newSubscriptionId, false, true));
                            dispatch(setIsAddingSubscription(false));
                            dispatch(nextSubscriptionStep(newSubscriptionId));

                            return;
                        } else {
                            const allowed = await updateSubscriptionAndPhoneNumber();
                            if (!allowed) return;
                        }
                    }
                    if (subscriptionId) {
                        dispatch(nextSubscriptionStep(subscriptionId));
                    }
                }}
                nextLabel={isSubscriptionChangeScopeChange ? "Fullfør" : "Fortsett"}
            />
        </div>
    );
};

export default ChooseSubscription;
